/**
 * AdvancedFilterBuilder — Boolean query builder for all module attributes.
 * Supports AND/OR groups, saved filters, and enum field auto-population.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import api from '../../services/api';
import { toast } from 'sonner';
import { Plus, Filter, Save, Star, StarOff, FolderOpen, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

import { NO_VALUE_OPS, mkCond, mkGroup } from './filterConstants';
import CondRow from './CondRow';
import SubGroup from './SubGroup';
import SaveDialog from './SaveDialog';

export default function AdvancedFilterBuilder({ module, reportId, onRunQuery, loading, initialFilter }) {
  const [fields, setFields] = useState([]);
  const [rootLogic, setRootLogic] = useState('AND');
  const [conditions, setConditions] = useState([mkCond()]);
  const [savedList, setSavedList] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const initialFilterApplied = React.useRef(false);

  useEffect(() => {
    if (!module) return;
    api.get('/api/saved-reports/schema/' + module).then(r => setFields(r.data.fields || [])).catch(() => {});
  }, [module]);

  // Load initialFilter from parent (e.g. when clicking a saved query from the home page)
  useEffect(() => {
    if (initialFilter && !initialFilterApplied.current) {
      initialFilterApplied.current = true;
      setRootLogic(initialFilter.logic || 'AND');
      setConditions(initialFilter.conditions?.length ? initialFilter.conditions : [mkCond()]);
    }
  }, [initialFilter]);

  // Reset the applied flag when module changes
  useEffect(() => { initialFilterApplied.current = false; }, [module]);

  const loadSaved = useCallback(() => {
    api.get('/api/saved-reports').then(r => {
      setSavedList((r.data || []).filter(s => s.module === module));
    }).catch(() => {});
  }, [module]);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  // Build filter spec from current state
  const buildSpec = () => {
    const cleaned = conditions.map(c => {
      if (c.logic) {
        const subs = c.conditions.filter(sc => sc.field && (NO_VALUE_OPS.has(sc.operator) || (sc.value !== '' && sc.value !== undefined)));
        return subs.length > 0 ? { logic: c.logic, conditions: subs } : null;
      }
      if (!c.field) return null;
      if (NO_VALUE_OPS.has(c.operator)) return c;
      if (c.operator === 'between') {
        if (!Array.isArray(c.value) || !c.value[0]) return null;
      } else if (c.value === '' || c.value === undefined) return null;
      return c;
    }).filter(Boolean);
    return { logic: rootLogic, conditions: cleaned };
  };

  const handleRun = () => onRunQuery(buildSpec());

  const handleSave = async (name) => {
    try {
      await api.post('/api/saved-reports', {
        module, report_id: reportId || '', name,
        filters: { logic: rootLogic, conditions }, starred: false,
      });
      toast.success('Saved "' + name + '"');
      setSaveOpen(false);
      loadSaved();
    } catch { toast.error('Failed to save'); }
  };

  const handleLoad = (saved) => {
    if (saved.filters) {
      setRootLogic(saved.filters.logic || 'AND');
      setConditions(saved.filters.conditions || [mkCond()]);
      toast.success('Loaded "' + saved.name + '"');
    }
  };

  const handleDelete = async (id, name) => {
    try {
      await api.delete('/api/saved-reports/' + id);
      toast.success('Deleted "' + name + '"');
      loadSaved();
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggleStar = async (id) => {
    try { await api.post('/api/saved-reports/' + id + '/star'); loadSaved(); } catch {}
  };

  const addCondition = () => setConditions(p => [...p, mkCond()]);
  const addGroup = () => setConditions(p => [...p, mkGroup(rootLogic === 'AND' ? 'OR' : 'AND')]);
  const handleClear = () => { setConditions([mkCond()]); setRootLogic('AND'); };

  const updCond = (i, c) => { const n = [...conditions]; n[i] = c; setConditions(n); };
  const rmCond = (i) => setConditions(conditions.filter((_, j) => j !== i));

  const starred = savedList.filter(s => s.starred);

  return (
    <div className="border rounded-xl bg-muted/20 mb-4" data-testid="advanced-filter-builder">
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/40 rounded-t-xl cursor-pointer"
        onClick={() => setExpanded(e => !e)} data-testid="filter-header-toggle">
        <Filter className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-semibold flex-1">Advanced Filter</span>
        <Badge variant="outline" className="text-[9px]">{fields.length} fields</Badge>
        {expanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
      </div>

      {expanded && (
        <div className="p-3 space-y-3">
          {/* Starred favourites */}
          {starred.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
              {starred.map(s => (
                <button key={s.id} onClick={() => handleLoad(s)}
                  className="text-[10px] px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 hover:bg-amber-100 font-medium"
                  data-testid={'favourite-' + s.id}>{s.name}</button>
              ))}
            </div>
          )}

          {/* Logic toggle + conditions */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Match</span>
              <div className="flex rounded-md overflow-hidden border text-[10px]">
                <button onClick={() => setRootLogic('AND')}
                  className={'px-2 py-0.5 font-semibold transition-colors ' + (rootLogic === 'AND' ? 'bg-primary text-primary-foreground' : 'bg-white hover:bg-muted')}
                  data-testid="filter-logic-and">ALL</button>
                <button onClick={() => setRootLogic('OR')}
                  className={'px-2 py-0.5 font-semibold transition-colors ' + (rootLogic === 'OR' ? 'bg-primary text-primary-foreground' : 'bg-white hover:bg-muted')}
                  data-testid="filter-logic-or">ANY</button>
              </div>
              <span className="text-[10px] text-muted-foreground">of the following:</span>
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="ghost" onClick={addCondition} className="h-5 text-[10px] px-1.5" data-testid="filter-add-condition">
                  <Plus className="h-3 w-3 mr-0.5" /> Condition
                </Button>
                <Button size="sm" variant="ghost" onClick={addGroup} className="h-5 text-[10px] px-1.5" data-testid="filter-add-group">
                  <Plus className="h-3 w-3 mr-0.5" /> Group
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              {conditions.map((c, i) => (
                c.logic
                  ? <SubGroup key={c.id} group={c} fields={fields} onChange={u => updCond(i, u)} onRemove={() => rmCond(i)} />
                  : <CondRow key={c.id} cond={c} fields={fields} onChange={u => updCond(i, u)} onRemove={() => rmCond(i)} />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1 border-t">
            <Button size="sm" onClick={handleRun} disabled={loading} data-testid="filter-run-btn">
              {loading ? 'Running...' : 'Run Query'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleClear} data-testid="filter-clear-btn">Clear</Button>
            <Button size="sm" variant="outline" onClick={() => setSaveOpen(true)} data-testid="filter-save-btn">
              <Save className="h-3.5 w-3.5 mr-1" /> Save Filter
            </Button>
            {savedList.length > 0 && (
              <div className="relative ml-auto group">
                <Button size="sm" variant="outline" className="gap-1" data-testid="filter-load-btn">
                  <FolderOpen className="h-3.5 w-3.5" /> Saved ({savedList.length}) <ChevronDown className="h-3 w-3" />
                </Button>
                <div className="absolute right-0 top-full mt-1 z-50 bg-white border rounded-lg shadow-lg w-64 max-h-56 overflow-y-auto hidden group-focus-within:block hover:block"
                  data-testid="saved-filters-dropdown">
                  {savedList.map(s => (
                    <div key={s.id} className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-accent/40 text-xs border-b last:border-0">
                      <button onClick={() => handleToggleStar(s.id)} className="shrink-0" data-testid={'star-' + s.id}>
                        {s.starred ? <Star className="h-3 w-3 text-amber-500 fill-amber-500" /> : <StarOff className="h-3 w-3 text-muted-foreground" />}
                      </button>
                      <button onClick={() => handleLoad(s)} className="flex-1 text-left truncate font-medium hover:text-primary"
                        data-testid={'load-' + s.id}>{s.name}</button>
                      <button onClick={() => handleDelete(s.id, s.name)} className="shrink-0 text-muted-foreground hover:text-destructive"
                        data-testid={'del-' + s.id}><Trash2 className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <SaveDialog open={saveOpen} onClose={() => setSaveOpen(false)} onSave={handleSave} />
    </div>
  );
}
